import type { HouseholdGroupResponse } from "../../../../api/src/household-groups/household-groups.service";

/**
 * The households that cannot join a household group from `effectiveDate`,
 * each with the names of the groups whose membership rows overlap the new
 * membership, in the order of `groups`.
 *
 * The new membership has no end date, so it covers every date from
 * `effectiveDate` on. A row overlaps it when the row's last day is null or on
 * or after `effectiveDate`. The last day is the earlier of the row's `endDate`
 * and the group's `dissolvedEffectiveDate`, or null when both are null: the
 * rule of `membershipLastDay` and `overlappingMemberships`
 * (`api/src/household-groups/household-group-refusals.ts`), so the client
 * never offers a household the api refuses with M1. Every group counts,
 * dissolved ones included. Dates are `YYYY-MM-DD`, so string order is date
 * order.
 */
export function householdGroupMembershipConflicts(
  groups: Pick<
    HouseholdGroupResponse,
    "name" | "dissolvedEffectiveDate" | "members"
  >[],
  effectiveDate: string,
): Map<number, string[]> {
  const conflicts = new Map<number, string[]>();
  for (const group of groups) {
    for (const member of group.members ?? []) {
      const lastDays = [member.endDate, group.dissolvedEffectiveDate].filter(
        (date): date is string => date != null,
      );
      const lastDay = lastDays.length > 0 ? lastDays.sort()[0] : null;
      if (lastDay !== null && lastDay < effectiveDate) {
        continue;
      }
      const groupNames = conflicts.get(member.householdId) ?? [];
      if (!groupNames.includes(group.name)) {
        groupNames.push(group.name);
      }
      conflicts.set(member.householdId, groupNames);
    }
  }
  return conflicts;
}
